/**
 * Product scan mode for IMEI / serial numbers.
 * DB table `scan_imei_sn_types.CODE` is the source of rows; this constant mirrors the seed
 * so validators and mappers stay typed. Adding a code = migration INSERT + a row here.
 * DCIx still speaks 0 / 1 (SN) / 2 (IMEI) — that is the WMS protocol, not the product column.
 *
 * Codes are camelCase, same shape as other GWMS product enums (finalProduct, preReceived).
 */

export const DEFAULT_IMEI_SN_REGEX = '^[0-9]{14,16}$';

export const SCAN_IMEI_SN_TYPE_CODES = [
    'none',
    'snExpedition',
    'imeiExpedition',
    'snInbound',
    'imeiInbound',
    'snBoth',
    'imeiBoth',
] as const;

export type ScanImeiSnType = (typeof SCAN_IMEI_SN_TYPE_CODES)[number];

export type DcixScanMode = 0 | 1 | 2;

export interface ScanImeiSnTypeDefinition {
    code: ScanImeiSnType;
    dcixExport: DcixScanMode;
    dcixImport: DcixScanMode;
    lockSnRegex: boolean;
    defaultSnRegex: string | null;
}

export const SCAN_IMEI_SN_TYPE_DEFINITIONS: Record<ScanImeiSnType, ScanImeiSnTypeDefinition> = {
    none: { code: 'none', dcixExport: 0, dcixImport: 0, lockSnRegex: false, defaultSnRegex: null },
    snExpedition: { code: 'snExpedition', dcixExport: 1, dcixImport: 0, lockSnRegex: false, defaultSnRegex: null },
    imeiExpedition: {
        code: 'imeiExpedition',
        dcixExport: 2,
        dcixImport: 0,
        lockSnRegex: true,
        defaultSnRegex: DEFAULT_IMEI_SN_REGEX,
    },
    snInbound: { code: 'snInbound', dcixExport: 0, dcixImport: 1, lockSnRegex: false, defaultSnRegex: null },
    imeiInbound: {
        code: 'imeiInbound',
        dcixExport: 0,
        dcixImport: 2,
        lockSnRegex: true,
        defaultSnRegex: DEFAULT_IMEI_SN_REGEX,
    },
    snBoth: { code: 'snBoth', dcixExport: 1, dcixImport: 1, lockSnRegex: false, defaultSnRegex: null },
    imeiBoth: {
        code: 'imeiBoth',
        dcixExport: 2,
        dcixImport: 2,
        lockSnRegex: true,
        defaultSnRegex: DEFAULT_IMEI_SN_REGEX,
    },
};

const LEGACY_SCAN_IMEI_SN: Record<0 | 1 | 2, ScanImeiSnType> = {
    0: 'none',
    1: 'snExpedition',
    2: 'imeiExpedition',
};

export function isScanImeiSnType(value: string): value is ScanImeiSnType {
    return (SCAN_IMEI_SN_TYPE_CODES as readonly string[]).includes(value);
}

export function getScanImeiSnTypeDefinition(code: string | null | undefined): ScanImeiSnTypeDefinition {
    if (code && isScanImeiSnType(code)) {
        return SCAN_IMEI_SN_TYPE_DEFINITIONS[code];
    }
    return SCAN_IMEI_SN_TYPE_DEFINITIONS.none;
}

/** Deprecated GWMS `scanImeiSn` (0/1/2) → codebook code. Unknown values become none. */
export function legacyScanImeiSnToType(value: number): ScanImeiSnType {
    if (value === 0 || value === 1 || value === 2) {
        return LEGACY_SCAN_IMEI_SN[value];
    }
    return 'none';
}

/** Deprecated boolean `scanSerialNumber`: true meant serial numbers on expedition. */
export function scanSerialNumberBooleanToType(value: boolean): ScanImeiSnType {
    return value ? 'snExpedition' : 'none';
}

/** Expedition projection kept on deprecated `SCAN_SERIAL_NUMBER` / GWMS `scanImeiSn`. */
export function expeditionScanModeFromType(code: string | null | undefined): DcixScanMode {
    return getScanImeiSnTypeDefinition(code).dcixExport;
}
